/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import moment from 'moment-timezone';
import { FilterMatchMode } from 'primereact/api';
import { showNotification } from '../../../redux/notification';
import refreshIcon from '../../../assets/images/refresh-purple.svg';
import DateRangeSelector from '../../../components/range-selector/DateRangeSelector';
import AgencyAssignModal from './assign-professionals/AgencyAssignModal';
import { useExternalAgencyOpenShiftsQuery } from '../../../redux/externalAgencyShifts';
import { applyFilters } from '../../../utils/FilterFunctions';
import FiltersSection from './FiltersSection';
import ShiftsCountBanner from './ShiftsCountBanner';
import {
	shiftTypeTemplate,
	rateTemplate,
	estimatedPayTemplate,
	startDateTemplate,
	timePeriodTemplate,
	facilityTemplate,
	buildFilterOptions,
	DEFAULT_FILTER_OPTIONS
} from './agency-utils';
import { user } from '../../../redux/user';

const AgencyOpenShifts = ({ searchTerm }) => {
	const timer = useRef(null);
	const dispatch = useDispatch();
	const [selectedRow, setSelectedRow] = useState(null);
	const [filterOptions, setFilterOptions] = useState(DEFAULT_FILTER_OPTIONS);
	const [filters, setFilters] = useState(DEFAULT_FILTER_OPTIONS);
	const [rangeStart, setRangeStart] = useState(new Date());
	const [rangeEnd, setRangeEnd] = useState(moment(rangeStart).add(13, 'day').toDate());
	const [shifts, setShifts] = useState([]);
	const [showAssignProfessionalModal, setShowAssignProfessional] = useState(null);
	const [latestAssignedShift, setLatestAssignedShift] = useState(null);
	const [assignedProfessionalAnimation, setAssignedProfessionalAnimation] = useState(false);
	const [globalFilter, setGlobalFilter] = useState({ global: { value: null, matchMode: FilterMatchMode.CONTAINS } });
	const userData = useSelector(user);

	const { data: openShifts, refetch: reloadOpenShifts } = useExternalAgencyOpenShiftsQuery(
		{ start: moment(rangeStart).format('YYYY-MM-DD'), end: moment(rangeEnd).format('YYYY-MM-DD') },
		{ refetchOnMountOrArgChange: true }
	);

	const totalSlots = shifts.reduce((acc, shift) => acc + Number(shift.slots_count), 0);

	useEffect(() => {
		setRangeEnd(moment(rangeStart).add(13, 'day'));

		return () => clearTimeout(timer.current);
	}, []);

	const handleAssignProfessionalModal = (shift) => {
		setShowAssignProfessional(true);
		setSelectedRow(shift);
	};

	useEffect(() => {
		if (openShifts) {
			const options = buildFilterOptions(openShifts, filters);
			setFilterOptions(options);
		}
	}, [openShifts]);

	useEffect(() => {
		const filteredShifts = applyFilters({ items: openShifts || [], filters });
		setShifts(filteredShifts);
	}, [openShifts, filters]);

	const updateFilters = (updatedOptions, updatedFilters) => {
		setFilterOptions(updatedOptions);
		setFilters(updatedFilters);
	};

	useEffect(() => {
		const _filter = { ...globalFilter };
		_filter.global.value = searchTerm;
		setGlobalFilter(_filter);
	}, [searchTerm]);

	const hasFiltersApplied = filters.facilities?.length || filters.shiftTypes?.length || !!filters.startTimes?.length;

	const professionalTemplate = (item) =>
		item.caregiverName ? (
			<div className="professional-name">{item.caregiverName}</div>
		) : (
			<div className="expand-button" onClick={() => handleAssignProfessionalModal(item)}>
				Assign
			</div>
		);

	const rowClass = (rowData) => {
		if (assignedProfessionalAnimation && rowData.shift_id === latestAssignedShift.shift_id) {
			if (latestAssignedShift.error) {
				if (latestAssignedShift.errorField === 'available_slot') {
					return 'remove-row-animation';
				}
				return 'highlight-error-row';
			}
			return 'highlight-assigned-row';
		}
		return '';
	};

	const _showNotification = (type, message) => dispatch(showNotification(type, message));

	const removeShift = (_shift) => {
		const newArray = shifts.filter((item) => item.shift_id !== _shift.shift_id);
		setShifts(newArray);
	};

	const updateShift = (_shift) => {
		const newArray = shifts.map((item) => {
			if (item.shift_id === _shift.shift_id) {
				return { ...item, slots_count: Number(item.slots_count) - 1, caregiverName: _shift.caregiverName };
			}
			return item;
		});
		setShifts(newArray);
	};

	const onAssignCompleted = (_shift) => {
		setShowAssignProfessional(false);
		setLatestAssignedShift(_shift);
		setAssignedProfessionalAnimation(true);
		setSelectedRow(null);

		let timeout = 2700;
		let shouldRemoveShift = false;
		if (!_shift.error) {
			updateShift(_shift);
		} else if (_shift.errorField === 'available_slot') {
			timeout = 1000;
			shouldRemoveShift = true;
		}

		timer.current = setTimeout(() => {
			setAssignedProfessionalAnimation(false);
			if (shouldRemoveShift) {
				removeShift(_shift);
			}
			setLatestAssignedShift(null);
		}, timeout);
	};

	return (
		<>
			<div className="toolbar">
				<DateRangeSelector
					rangeStart={rangeStart}
					rangeEnd={rangeEnd}
					setRangeStart={setRangeStart}
					setRangeEnd={setRangeEnd}
					minDate={new Date()}
					proTip={
						<span>
							<b>Pro tip:</b> We limit shifts (rows) shown to {userData.agencyPerDiemShiftFeedLimit} regardless of the
							date range. Try selecting a smaller date range if you see the limit reached warning at the bottom of your
							feed.
						</span>
					}
				/>

				<button type="button" className="btn refresh-button" onClick={reloadOpenShifts}>
					<img src={refreshIcon} alt="Refresh" />
					<div className="button-label">Refresh Feed</div>
				</button>
				<div className="flex-one" />
				<FiltersSection
					filterOptions={filterOptions}
					filters={filters}
					updateFilters={updateFilters}
					isDisabled={!!globalFilter.global.value}
				/>
			</div>
			<div className="datatable-wrapper">
				<div className="wrapper-container">
					<DataTable
						value={shifts}
						scrollable
						scrollHeight="flex"
						sortMode="multiple"
						selectionMode="single"
						selection={selectedRow}
						dataKey="shift_id"
						virtualScrollerOptions={{ itemSize: 65 }}
						tableStyle={{ minWidth: '100rem' }}
						size="small"
						stripedRows
						cellClassName="table-cell"
						rowClassName={rowClass}
						footer={
							shifts?.length ? (
								<ShiftsCountBanner
									totalSlots={totalSlots}
									shiftsCount={shifts.length}
									filtersApplied={hasFiltersApplied}
									shiftFeedLimit={userData.agencyPerDiemShiftFeedLimit}
								/>
							) : null
						}
						filters={globalFilter}
					>
						<Column field="start_time" header="Date" style={{ width: '14%' }} sortable body={startDateTemplate} />
						<Column field="shift_type" header="Shift type" style={{ width: '10%' }} sortable body={shiftTypeTemplate} />
						<Column field="slots_count" header="Open" style={{ width: '5%' }} sortable />
						<Column field="facility_management_company_name" header="Management" style={{ width: '15%' }} sortable />
						<Column field="facility_name" header="Facility" style={{ width: '15%' }} sortable body={facilityTemplate} />
						<Column field="start_hour_tz" header="Time" style={{ width: '14%' }} body={timePeriodTemplate} />
						<Column
							field="estimated_pay"
							header="Est. pay"
							style={{ width: '6%' }}
							sortable
							body={estimatedPayTemplate}
						/>
						<Column field="rate" header="Rate" style={{ width: '6%' }} sortable body={rateTemplate} />
						<Column field="facility_city" header="City" style={{ width: '10%' }} sortable />
						<Column field="facility_state_code" header="State" style={{ width: '5%' }} sortable />
						<Column field="facility_zip_code" header="ZIP" style={{ width: '5%' }} sortable />
						<Column
							field="professional"
							header="Actions"
							style={{ width: '8%' }}
							alignFrozen="right"
							frozen
							body={professionalTemplate}
						/>
					</DataTable>

					{showAssignProfessionalModal && (
						<AgencyAssignModal
							shift={selectedRow}
							onCancel={() => setShowAssignProfessional(false)}
							onComplete={onAssignCompleted}
							showNotification={_showNotification}
							dateTemplate={startDateTemplate}
						/>
					)}
				</div>
			</div>
		</>
	);
};

export default AgencyOpenShifts;
